import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Mail, Send, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import api from '../../lib/api';
import { formatDateDMY } from '../../lib/utils';

export const RentaEmailConfigButton = () => {
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get('/liquidacion/renta-email-config').then(r => setCfg(r.data)).catch(() => toast.error('No se pudo cargar la configuración'));
  }, [open]);

  const set = (k) => (e) => setCfg(prev => ({ ...prev, [k]: e.target.value }));
  const guardar = async () => {
    setSaving(true);
    try {
      const r = await api.put('/liquidacion/renta-email-config', { email_remitente: cfg.email_remitente, asunto: cfg.asunto, cuerpo: cfg.cuerpo });
      setCfg(prev => ({ ...prev, ...r.data }));
      toast.success('Email de aviso guardado');
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar');
    } finally { setSaving(false); }
  };

  return (
    <>
      <Button variant="outline" size="sm" className="h-8 ml-2" onClick={() => setOpen(true)} data-testid="renta-email-config-btn">
        <Mail className="w-4 h-4 mr-1.5" /> Configurar email
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="renta-email-config-dialog">
          <DialogHeader>
            <DialogTitle>Email de aviso de renta al propietario</DialogTitle>
            <DialogDescription>
              Se envía automáticamente al publicar la renta. Sale con el nombre <b>{cfg?.from_name || 'Global Citizen'}</b> e incluye un botón "Ver mi renta".
            </DialogDescription>
          </DialogHeader>
          {!cfg ? <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div> : (
            <div className="space-y-3">
              <div><Label>Email (las respuestas llegan acá)</Label><Input value={cfg.email_remitente} onChange={set('email_remitente')} data-testid="renta-email-remitente" /></div>
              <div><Label>Asunto</Label><Input value={cfg.asunto} onChange={set('asunto')} data-testid="renta-email-asunto" /></div>
              <div>
                <Label>Cuerpo del email</Label>
                <Textarea rows={10} value={cfg.cuerpo} onChange={set('cuerpo')} data-testid="renta-email-cuerpo" />
                <p className="text-xs text-slate-500 mt-1">Podés usar: {(cfg.variables || []).map(v => `{${v}}`).join(' ')}. Los montos son la parte de cada propietario.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="renta-email-cancelar">Cancelar</Button>
            <Button onClick={guardar} disabled={!cfg || saving} data-testid="renta-email-guardar">
              {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const AvisoRentaCell = ({ edificioId, periodo, unidadIds, publicado, enviadaAt, error, onSent, testId }) => {
  const [sending, setSending] = useState(false);
  if (!publicado) return <span className="text-xs text-slate-300">-</span>;
  const enviar = async () => {
    if (enviadaAt && !window.confirm(`El aviso ya se envió el ${formatDateDMY(enviadaAt.slice(0, 10))}. ¿Enviarlo de nuevo?`)) return;
    setSending(true);
    try {
      const r = await api.post(`/liquidacion/${edificioId}/enviar-aviso-renta`, { periodo, unidad_ids: unidadIds });
      toast.success(`Aviso enviado a ${r.data.enviados.join(', ')}`);
      if (r.data.errores.length) toast.warning(r.data.errores.join(' · '));
      onSent?.(r.data.enviada_at);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo enviar el aviso');
    } finally { setSending(false); }
  };
  return (
    <div className="flex flex-col items-center gap-0.5" data-testid={testId}>
      {enviadaAt
        ? <span className="text-xs text-emerald-700 font-medium" data-testid={`${testId}-fecha`}>{formatDateDMY(enviadaAt.slice(0, 10))}</span>
        : error && <span className="text-[11px] text-rose-600 max-w-[140px] truncate" title={error}>{error}</span>}
      <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px] text-slate-500" onClick={enviar} disabled={sending} data-testid={`${testId}-btn`}>
        {sending ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
        {enviadaAt ? 'Reenviar' : 'Enviar'}
      </Button>
    </div>
  );
};
